import React, {useEffect, useLayoutEffect, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import './App.scss';
import './Mobile.scss';
import MaterialTrainingTool from './components/MaterialTrainingTool/MaterialTrainingTool';
import MaterialProjectSelect from './components/MaterialProjectSelect/MaterialProjectSelect';
import packageInfo from '../package.json';
import {ReactKeycloakProvider} from '@react-keycloak/web';
import User from './models/User';
import keycloak from './api/Keycloak';
import {useSessionKeepalive} from './api/useSessionKeepalive';
import {useTranslation} from 'react-i18next';
import Header from './components/Header/Header';
import CoachingKangaroo from "./api/CoachingKangaroo";
import MaintenanceDialog from './components/MaintenanceDialog/MaintenanceDialog';

const useStyles = (theme) => ({
    root: {
        flexGrow: 1,
    },
    paper: {
        padding: theme.spacing(2),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
});

const App = () => {
    const [, setWindowWidth] = useState(0);
    const [, setWindowHeight] = useState(0);
    const [projects, setProjects] = useState([]);
    const [projectName, setProjectName] = useState('');
    const [projectId, setProjectId] = useState("")
    const [user, setUser] = useState(new User());
    const [loginError, setError] = useState('');
    const {i18n} = useTranslation();
    const classes = useStyles;

    const [selectedLanguage, setSelectedLanguage] = useState('cz');
    const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);

    // Covers the case the axios interceptor cannot: long stretches of work that never reach the
    // backend. No-ops until there is a session to keep alive.
    useSessionKeepalive();

    const resizeWindow = () => {
        setWindowWidth(window.innerWidth);
        setWindowHeight(window.innerHeight);
    };

    useLayoutEffect(() => {
        window.addEventListener('resize', resizeWindow);
        resizeWindow();
        return () => window.removeEventListener('resize', resizeWindow);
    }, []);

    const handleOnEvent = async (event) => {
        if (event === 'onAuthSuccess') {
            if (keycloak.authenticated) {
                const userInfo = await keycloak.loadUserInfo();
                const context =
                    keycloak.tokenParsed.context !== undefined
                        ? keycloak.tokenParsed.context.split(';')
                        : [];
                const clientRoles = keycloak.realmAccess.roles.filter(
                    (role) => role.startsWith('ADMIN') || role.startsWith('TRAINING')
                );
                getProjects();
                if (isSingleMode()) {
                    setSingleModeProject();
                } else {
                    getProjects();
                }
                let keycloakUser = new User(
                    userInfo.preferred_username,
                    userInfo.given_name,
                    userInfo.family_name,
                    userInfo.email,
                    clientRoles,
                    context
                );
                setUser(keycloakUser);
                if (isMaintenanceEnabled()) {
                    setMaintenanceDialogOpen(true);
                }
            }
        }

        // No onAuthLogout/onAuthRefreshError branch here on purpose. With onLoad: 'login-required'
        // keycloak-js sets loginRequired, and clearToken() then calls login() itself - and
        // clearToken() is the only thing that raises onAuthLogout. Handling it here would just start
        // a second, competing navigation. A refresh that failed because Keycloak is unreachable
        // leaves the token in place and does not clear it, so nothing fires and the user stays put,
        // which is what we want.
    };

    const isMaintenanceEnabled = () => {
        if (!window.g_maintenanceEnabled?.startsWith('%')) {
            return window.g_maintenanceEnabled === 'true';
        }
        return process.env.REACT_APP_MAINTENANCE_ENABLED === 'true';
    };

    const isSingleMode = () => {
        if (!window.g_singleMode?.startsWith('%'))
            return Boolean(window.g_singleMode) !== false;
        else return process.env.REACT_APP_SINGLE_MODE === true;
    };

    const setSingleModeProject = () => {
        let projectId = !window.g_singleModeProjectId?.startsWith('%')
            ? window.g_singleModeProjectId
            : process.env.REACT_APP_PROJECT_ID;
        let projectName = !window.g_singleModeProjectName?.startsWith('%')
            ? window.g_singleModeProjectName
            : process.env.REACT_APP_PROJECT_NAME;

        setProjects([{id: projectId, name: projectName}]);
        setProjectName(projectName);
    };

    const getProjects = () => {
        // No Authorization here on purpose: the request interceptor sets it after refreshing, and
        // passing it in was misleading - the interceptor overwrote it with the same value anyway.
        CoachingKangaroo.get('/api/v1/projects')
            .then((response) => {
                setProjects(response.data);
                const savedData = JSON.parse(localStorage.getItem("tt-project"));
                let itemFound = false;
                if (savedData) {
                    response.data.forEach((project) => {
                        if (project.id === savedData) {
                            handleProjectChange(project.name, project.id);
                            itemFound = true;
                        }
                    });

                    if (!itemFound) {
                        setProjectId(response.data[0].id)
                        setProjectName(response.data[0].name);
                    }
                } else {
                    setProjectId(response.data[0].id)
                    setProjectName(response.data[0].name);
                }
            })
            .catch(() => {
                setError('Error while fetching data');
            });
    };

    const switchLoggedInState = () => {
        setProjects([]);
        setProjectName('');
        setUser(null);
        keycloak.logout();
    };

    const handleLangSwitch = (lang) => {
        setSelectedLanguage(lang);
        i18n.changeLanguage(lang);
    };

    const handleProjectChange = (name, id) => {
        localStorage.setItem("tt-project", JSON.stringify(id));
        setProjectId(id)
        setProjectName(name);
    };
    const {t} = useTranslation();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const filter = params.get('filter')
        if (filter && filter.length > 0) {
            sessionStorage.setItem('filter', filter)
            const url = new URL(window.location.href);
            url.searchParams.delete("filter");
            window.history.replaceState({}, document.title, url.toString());        }

    }, []);

    return (
        <ReactKeycloakProvider
            className={classes.root}
            authClient={keycloak}
            // The provider's own refresh fires only once the token has already expired, so requests
            // sent in the gap before it completes still carry a dead token. It is also a bare timer
            // that would keep the SSO session alive from a forgotten tab. Both jobs are handled by
            // the axios interceptor and the keepalive hook instead.
            autoRefreshToken={false}
            initOptions={{
                onLoad: 'login-required',
                checkLoginIframe: false,
            }}
            onEvent={(event, error) => handleOnEvent(event, error)}
        >
            <div className={'flex flex-col w-full App min-h-screen ' + (keycloak.authenticated ? 'loggedIn' : 'd')}>
                <Header
                    user={user}
                    selectedLanguage={selectedLanguage}
                    handleLangSwitch={handleLangSwitch}
                    switchLoggedInState={switchLoggedInState}
                    loginError={loginError}
                />
                <div className="flex flex-col ml-[50px]" style={{width: 'calc(100% - 50px)'}}>
                    {user ? (
                        <>
                            <div className="flex gap-1.5 items-center mb-4 px-4 mt-3">
                                <img 
                                    src="/training_tool_ico.png" 
                                    alt="Training Tool" 
                                    style={{ 
                                        width: 'auto', 
                                        height: '24px', 
                                        marginRight: '8px',
                                        filter: 'brightness(0) saturate(100%) invert(0%)',
                                        opacity: 0.87,
                                        objectFit: 'contain'
                                    }} 
                                />
                                <h2 className="font-light text-lg lg:text-2xl mb-0">Training Tool <span style={{ margin: '0 8px' }}>|</span> {t('Training')}</h2>
                                <MaterialProjectSelect
                                    projects={projects}
                                    selectedProjectName={projectName}
                                    handleChange={handleProjectChange}
                                />
                            </div>
                        </>
                    ) : null}
                    {projectName ? (
                        <div className="flex flex-row w-full relative" style={{minHeight: 'calc(100vh - 150px)'}}>
                            <MaterialTrainingTool
                                project={projectName}
                                projectId={projectId}
                                context={user.context}
                            />
                        </div>
                    ) : null}
                </div>

                <footer className="flex bg-[#e2e2e2] mt-auto ml-[50px]" style={{width: 'calc(100% - 50px)'}}>
                    <span
                        className="block mx-auto py-1.5 opacity-80">Made by AddAI team. Version: {packageInfo.version}</span>
                </footer>
            </div>
            <MaintenanceDialog
                open={maintenanceDialogOpen}
                onClose={() => setMaintenanceDialogOpen(false)}
            />
        </ReactKeycloakProvider>
    );
};

export default App;
